import React, { useEffect } from "react";
import { Form, Input, Button, Card, message, Space, Typography } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { resetPassword, clearError } from "../../store/authSlice";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import "./AuthForms.scss";
import Logo from "../../asset/images/logo.png";

const { Title, Text } = Typography;

interface ResetPasswordFormData {
  password: string;
  confirm_password: string;
}

const ResetPasswordForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isLoading, error } = useAppSelector((state) => state.auth);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      message.error("Invalid reset link");
      navigate("/login");
    }
  }, [token, navigate]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleSubmit = async (values: ResetPasswordFormData) => {
    if (!token) {
      message.error("Invalid reset token");
      return;
    }

    try {
      const result = await dispatch(
        resetPassword({
          token,
          password: values.password,
          confirmPassword: values.confirm_password,
        })
      ).unwrap();

      message.success(result.message || "Password reset successful!");

      // Redirect to login page after successful reset
      setTimeout(() => {
        navigate("/login");
      }, 1500);
    } catch (err) {
      // Error is handled by Redux and shown via useEffect
    }
  };

  const validateConfirmPassword = ({ getFieldValue }: any) => ({
    validator(_: any, value: string) {
      if (!value || getFieldValue("password") === value) {
        return Promise.resolve();
      }
      return Promise.reject(new Error("Passwords do not match"));
    },
  });

  if (!token) {
    return null; // or loading component
  }

  return (
    <div className="auth-container">
      <Card className="auth-card">
        <div className="auth-header">
          <img
            src={Logo}
            style={{ height: "35px", marginBottom: "10px" }}
            alt="Logo"
          />
          <br />
          <Title level={3} className="auth-title">
            Reset your password
          </Title>
          <Text className="auth-subtitle">Enter your new password below</Text>
        </div>

        <Form
          form={form}
          name="reset-password"
          onFinish={handleSubmit}
          layout="vertical"
          size="middle"
          className="auth-form"
        >
          <Form.Item
            name="password"
            label="New Password"
            rules={[
              { required: true, message: "Please enter your new password" },
              { min: 8, message: "Password must be at least 8 characters" },
              // {
              //   pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
              //   message: "Password must contain at least one uppercase letter, one lowercase letter, and one number",
              // },
            ]}
            hasFeedback
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter your new password"
            />
          </Form.Item>

          <Form.Item
            name="confirm_password"
            label="Confirm New Password"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Please confirm your new password" },
              validateConfirmPassword,
            ]}
            hasFeedback
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Confirm your new password"
            />
          </Form.Item>

          <Form.Item className="auth-submit">
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              block
              className="auth-button"
            >
              {isLoading ? "Resetting..." : "Reset Password"}
            </Button>
          </Form.Item>
        </Form>

        <div className="auth-footer">
          <Space direction="vertical" align="center" className="w-100">
            <Link to="/login" className="auth-link">
              Back to Sign In
            </Link>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default ResetPasswordForm;
